import baustein from "baustein";
import analytics from "web/script/analytics/analytics";
import facebookEvents from "web/script/analytics/facebook-events";
import overlayMixin from "web/script/components/mixins/overlay-mixin";
import environment from "web/script/modules/environment";
import rothko from "web/script/modules/rothko";
import userProfiler from "web/script/modules/userprofiler";
import browser from "web/script/utils/browser";
import history from "web/script/utils/history";
import { base64Decode } from "web/script/utils/json";
import logging from "web/script/utils/logging";
import globalEvents from "web/script/utils/global-events";

import "web/script/components/email-capture-prompt";
import "web/script/components/follow-button";
import "web/script/components/overlays/generic-overlay-launcher";
import "web/script/components/product-overview";
import "web/script/components/read-more-text";
import "web/script/components/save-for-later-button";
import "web/script/components/save-for-later-signup-prompt";
import renderReactComponentInDocument from "web/script/utils/render-react";
import ProductArea from "web/react/components/product-area/product-area";
import PDPGallery from "web/react/components/pdp-gallery/pdp-gallery";
import RelatedProductsLoader from "web/react/components/related-products-loader/related-products-loader";
import RecentlyViewed from "web/react/components/recently-viewed/recently-viewed";

export default baustein.register("product-overlay", {
    mixins: [overlayMixin],

    defaultOptions: {
        // overlay-mixin options
        overlaySize: "large",
        noContentPadding: true,

        // product-overlay options
        productSlug: null,
        productUid: null,
        linkId: null,
        paid: null,
    },

    setupEvents(add) {
        add("selected", "product-card", this._onProductCardSelect);

        this._boundProductCardSelect = this._onProductCardSelect.bind(this);
        this._boundAddedToBag = this._onAddedToBag.bind(this);

        globalEvents.on("product-card-selected", this._boundProductCardSelect);
        globalEvents.on("added-to-bag", this._boundAddedToBag);
    },

    onRemove() {
        globalEvents.off("product-card-selected", this._boundProductCardSelect);
        globalEvents.off("added-to-bag", this._boundAddedToBag);
    },

    renderProductArea() {
        if (document.querySelector(".product-in-stock-overlay__details")) {
            if (this.overlayContext.in_stock) {
                const inStock = this.overlayContext.in_stock;
                const productArea = inStock["product_area"];
                const productEditButton = inStock["product_edit_button"];
                renderReactComponentInDocument(
                    ProductArea,
                    {
                        productArea: productArea,
                        editButton: productEditButton,
                        features: productArea.features,
                    },
                    document.querySelector(".product-in-stock-overlay__details")
                );
            }
        }
    },

    renderImageGallery() {
        if (
            document.querySelector(".product-in-stock-overlay__gallery__slot") &&
            this.overlayContext.in_stock
        ) {
            const inStock = this.overlayContext.in_stock;
            const productImageGallery = inStock["product_image_gallery"];

            renderReactComponentInDocument(
                PDPGallery,
                {
                    product: productImageGallery,
                    features: {},
                },
                document.querySelector(".product-in-stock-overlay__gallery__slot")
            );
        }

        if (
            document.querySelector(".product-overview__gallery__slot") &&
            this.overlayContext.in_stock
        ) {
            const inStockOverview = this.overlayContext.in_stock.product_overview;
            const productImageGallery = inStockOverview["product_image_gallery"];

            renderReactComponentInDocument(
                PDPGallery,
                {
                    product: productImageGallery,
                    features: {},
                },
                document.querySelector(".product-overview__gallery__slot")
            );
        }

        if (
            document.querySelector(".product-oos-overlay__info__gallery__slot") &&
            this.overlayContext.out_of_stock
        ) {
            const outOfStock = this.overlayContext.out_of_stock;
            const productImageGallery = outOfStock["product_image_gallery"];

            renderReactComponentInDocument(
                PDPGallery,
                {
                    product: productImageGallery,
                    features: {},
                    variant: "oos-carousel",
                },
                document.querySelector(".product-oos-overlay__info__gallery__slot")
            );
        }
    },

    renderRelatedProductsLoader() {
        if (
            document.querySelector(".product-in-stock-overlay__related-products__slot") &&
            this.overlayContext.in_stock
        ) {
            const inStock = this.overlayContext.in_stock;
            const relatedProductsLoader = inStock["related_products_loader"];

            renderReactComponentInDocument(
                RelatedProductsLoader,
                { ...relatedProductsLoader },
                document.querySelector(".product-in-stock-overlay__related-products__slot")
            );
        }

        if (
            document.querySelector(".product-oos-overlay__info__related-products__slot") &&
            this.overlayContext.out_of_stock
        ) {
            const outOfStock = this.overlayContext.out_of_stock;
            const relatedProductsLoader = outOfStock["related_products_loader"];

            renderReactComponentInDocument(
                RelatedProductsLoader,
                { ...relatedProductsLoader },
                document.querySelector(".product-oos-overlay__info__related-products__slot")
            );
        }
    },

    renderRecentlyViewed() {
        if (
            document.querySelector(".product-in-stock-overlay__recently-viewed__slot") &&
            this.overlayContext.in_stock
        ) {
            const inStock = this.overlayContext.in_stock;
            const recentlyViewedProducts = inStock["recently_viewed_products"];

            renderReactComponentInDocument(
                RecentlyViewed,
                {
                    ...recentlyViewedProducts,
                },
                document.querySelector(".product-in-stock-overlay__recently-viewed__slot")
            );
        }

        if (
            document.querySelector(".product-oos-overlay__recently-viewed__slot") &&
            this.overlayContext.out_of_stock
        ) {
            const outOfStock = this.overlayContext.out_of_stock;
            const recentlyViewedProducts = outOfStock["recently_viewed_products"];

            renderReactComponentInDocument(
                RecentlyViewed,
                {
                    ...recentlyViewedProducts,
                },
                document.querySelector(".product-oos-overlay__recently-viewed__slot")
            );
        }
    },

    onOptionsChange() {
        if (!this.el) {
            return;
        }

        this.el.scrollTop = 0;

        this.getOverlayContent()
            .then((content) => {
                this.setContent(content);
                this.renderProductArea();
                this.renderImageGallery();
                this.renderRelatedProductsLoader();
                this.renderRecentlyViewed();
            })
            .then(this.trackPageView.bind(this));
    },

    onOverlayOpen() {
        this.renderProductArea();
        this.renderImageGallery();
        this.renderRelatedProductsLoader();
        this.renderRecentlyViewed();
        this.trackPageView();
        // Fix page on mobile to avoid background scrolling on ios
        if (browser.getViewport() === browser.VIEWPORTS.MOBILE) {
            this.emit("fixPageContent");
        }
    },

    onOverlayCloseIntent() {
        history.back();
        if (browser.getViewport() === browser.VIEWPORTS.MOBILE) {
            this.emit("unFixPageContent");
        }
    },

    trackPageView() {
        const container = this.el.querySelector(".product-overlay");
        if (!container) {
            console.warn("Cannot track page view for product overlay!");
            return;
        }

        const analyticsProductData = base64Decode(container.getAttribute("data-analytics-product"));

        // Save the seen product in the
        // user localstorage profile
        userProfiler.saveSeenProduct(analyticsProductData);

        // update environment variables
        environment.set({
            gaVariables: base64Decode(container.getAttribute("data-ga-dimensions")),
            analyticsProduct: analyticsProductData,
            pageType: "product_overlay",
            pageSubType: analyticsProductData.soldout ? "out_of_stock" : "in_stock",
            pageViewAnalytics: base64Decode(container.getAttribute("data-page-view")),
        });

        // we add adroll via tag manager so don't know if it's there yet and we only want to use
        // them temporary so we are doing this the ugly way. will be better with doubleclick (haha, lol)
        try {
            window.__adroll.record_user({
                product_id: analyticsProductData.product_slug,
            });
        } catch (err) {
            // do nothing
        }

        analytics.tagEvent(analytics.TAG_NETWORKS.FACEBOOK, facebookEvents.VIEW_CONTENT, {
            content_ids: [analyticsProductData.product_id],
            content_type: "product",
        });
    },

    getOverlayContent() {
        const slug = this.options.productSlug;
        const uid = this.options.productUid;
        const linkId = this.options.linkId;
        const paid = this.options.paid;

        return Promise.all([
            import(
                /* webpackChunkName: "template-product-overlay" */
                "templates/modules/product/product_overlay.jinja"
            ),
            rothko.fetch("modules/product/product_overlay", {
                slug,
                uid,
                link_id: linkId,
                paid,
            }),
        ])
            .then(([{ default: template }, productOverlayContext]) => {
                this.overlayContext = productOverlayContext;
                environment.set("features", {
                    checkout_buy_now: this.overlayContext.checkout_buy_now,
                    checkp_raw_sizes: this.overlayContext.checkp_raw_sizes,
                });
                return template({
                    product_overlay: this.overlayContext,
                    csrf_token: rothko.getCSRF(),
                });
            })
            .catch((err) => {
                logging.error(err);
            });
    },

    getOverlayContext() {
        return this.overlayContext;
    },

    /**
     * Handles the 'selected' event from product-card components. Stops propagation so the event
     * does not bubble up any further.
     * @private
     */
    _onProductCardSelect(event) {
        event.stopPropagation && event.stopPropagation();
    },

    _onAddedToBag() {
        history.back();
    },
});
